// note-edit.js -- Peteramati JavaScript library
// Peteramati is Copyright (c) 2006-2024 Eddie Kohler
// See LICENSE for open-source distribution terms

import { escape_entities, regexp_quote } from "./encoders.js";
import { hoturl } from "./hoturl.js";
import { hasClass, addClass, removeClass, handle_ui } from "./ui.js";
import { event_key } from "./ui-key.js";
import { Linediff } from "./diff.js";
import { Note } from "./note.js";
import { GradeSheet } from "./gradeentry.js";
import { ftext, render_onto } from "./render.js";
import { tooltip, global_tooltip } from "./tooltip.js";


let curline, curgrade, down_event, capture_scroll_at;

// A `scroll` event within this many ms of one of our own programmatic scrolls
// (or of opening capture) is treated as ours — or as follow-on reflow from an
// animation/height change — rather than a deliberate user scroll.
const CAPTURE_SCROLL_GRACE = 300;

function locate(e) {
    while (e && e.tagName !== "TEXTAREA" && e.tagName !== "A") {
        if (hasClass(e, "pa-dl")) {
            for (let ln of Linediff.all(e)) {
                if (ln.is_note()) {
                    return ln;
                } else if (ln.element !== e && ln.is_visible() && !ln.is_annotation()) {
                    break;
                }
            }
            return new Linediff(e);
        }
        e = e.parentElement;
    }
    return null;
}

function render_form($tr, note, transition) {
    $tr[0].hidden = false;
    $tr.addClass("editing");
    var $td = $tr.find(".pa-notebox");
    if (transition) {
        $tr.css("display", "").children().css("display", "");
        var $content = $td.children();
        $content.slideUp(80).queue(function () { $content.remove(); });
    }

    let gi = GradeSheet.closest(curline.element),
        format = note ? note.format : null;
    if (format == null) {
        format = document.body.getAttribute("data-default-format");
    }
    let t = '<form method="post" action="'.concat(
        escape_entities(hoturl("=api/linenote", {psetinfo: gi.element, file: curline.file, line: curline.note_lineid, oldversion: (note && note.version) || 0, format: format})),
        '" enctype="multipart/form-data" accept-charset="UTF-8" class="ui-submit pa-noteform">',
        '<textarea class="pa-note-entry need-autogrow" name="note"></textarea>',
        '<div class="aab aabr pa-note-aa justify-content-between">',
        '<div class="aabutr order-100"><button class="btn-primary" type="submit">Save comment</button></div>',
        '<div class="aabutr order-99"><button type="button" name="cancel">Cancel</button></div>',
        '<div class="aabut"><button type="button" class="btn ui pa-load-note-suggestions">↡</button></div>');
    if (!gi.scores_visible) {
        t += '<div class="aabut"><label class="checki"><input type="checkbox" name="iscomment" value="1" class="checkc">Show immediately</label></div>';
    }
    t += '<div class="aabut flex-grow-1"></div>';
    var $form = $(t + '</div></form>').appendTo($td);

    var $ta = $form.find("textarea");
    if (note && note.ftext !== null) {
        const text = note.editable_text;
        $ta.text(text);
        $ta[0].setSelectionRange && $ta[0].setSelectionRange(text.length, text.length);
    }
    $ta.autogrow().keydown(textarea_keydown);
    $form.find("input[name=iscomment]").prop("checked", !!(note && note.iscomment));
    $form.find("button[name=cancel]").click(cancel);
    $form.on("submit", function () {
        pa_save_my_note(this.closest(".pa-dl"));
    });
    if (transition) {
        $ta.focus();
        $form.hide().slideDown(100);
    }
}

function is_form_field(el) {
    if (!el) {
        return false;
    }
    const tn = el.nodeName;
    return tn === "INPUT" || tn === "TEXTAREA" || tn === "SELECT"
        || el.isContentEditable;
}


// ---- sidebar gradebox keyboard navigation -------------------------------

function gradebox_entries(gb) {
    const out = [];
    for (const e of gb.children) {
        if (hasClass(e, "pa-grade") && hasClass(e, "pa-p")
            && !hasClass(e, "pa-p-hidden")
            && e.querySelector(".pa-gradevalue")) {
            out.push(e);
        }
    }
    return out;
}

function focused_gradebox_entry(ae, gb) {
    const e = ae && ae.closest && ae.closest(".pa-grade.pa-p");
    return e && e.parentElement === gb ? e : null;
}

function left_navigates_out(el) {
    if (el.nodeName === "TEXTAREA"
        || (el.nodeName === "INPUT"
            && /^(?:text|search|email|url|tel|password|number)$/.test(el.type))) {
        try {
            return el.selectionStart === 0 && el.selectionEnd === 0;
        } catch {
            return true;
        }
    }
    return true;
}

function gradebox_key(evt) {
    if (event_key.is_modifier(evt) || event_key.modcode(evt)) {
        return;
    }
    const key = event_key(evt);
    if (key !== "ArrowUp" && key !== "ArrowDown" && key !== "ArrowLeft") {
        return;
    }
    const gb = this, ae = document.activeElement;
    if (!ae || !gb.contains(ae)) {
        return;
    }
    if (key === "ArrowLeft") {
        if (!left_navigates_out(ae)) {
            return;
        }
        const live = document.querySelector(".pa-dllive");
        if (live) {
            live.focus({preventScroll: true});
            evt.preventDefault();
        }
        return;
    }
    if (ae.nodeName === "TEXTAREA") {
        return; // multi-line entry: defer to textarea caret movement
    }
    const cur = focused_gradebox_entry(ae, gb);
    if (!cur) {
        return;
    }
    const entries = gradebox_entries(gb),
        idx = entries.indexOf(cur),
        next = idx >= 0 ? entries[idx + (key === "ArrowDown" ? 1 : -1)] : null;
    if (next) {
        const input = next.querySelector(".pa-gradevalue");
        if (input) {
            input.focus();
            evt.preventDefault();
        }
    }
}

function gradebox_focusin(evt) {
    const entry = focused_gradebox_entry(evt.target, this);
    if (!entry) {
        return;
    }
    for (const e of this.children) {
        if (e !== entry && hasClass(e, "pa-graderecent")) {
            removeClass(e, "pa-graderecent");
        }
    }
    addClass(entry, "pa-graderecent");
}

function enter_sidebar_gradebox_from(lineEl) {
    const sb = lineEl.closest(".pa-with-sidebar");
    if (!sb) {
        return false;
    }
    const gb = sb.querySelector(":scope > .pa-sidebar > .pa-gradebox:not(.pa-filenavbox)");
    if (!gb) {
        return false;
    }
    const entries = gradebox_entries(gb);
    if (!entries.length) {
        return false;
    }
    const remembered = gb.querySelector(":scope > .pa-grade.pa-p.pa-graderecent"),
        target = remembered && entries.indexOf(remembered) >= 0 ? remembered : entries[0],
        input = target.querySelector(".pa-gradevalue");
    if (!input) {
        return false;
    }
    input.focus();
    // For text-like inputs and textareas, select the existing value so the
    // first keystroke overwrites it. `<select>` has no .select() method;
    // for non-text input types it's a no-op.
    if (input.select) {
        requestAnimationFrame(() => input.select());
    }
    return true;
}

$(document).on("keydown", ".pa-sidebar > .pa-gradebox:not(.pa-filenavbox)", gradebox_key);
$(document).on("focusin", ".pa-sidebar > .pa-gradebox:not(.pa-filenavbox)", gradebox_focusin);

// -------------------------------------------------------------------------


function arrowcapture(evt) {
    if ((evt.type === "keydown"
         && event_key.is_modifier(evt))
        || (evt.type === "scroll"
            && capture_scroll_at
            && performance.now() - capture_scroll_at <= CAPTURE_SCROLL_GRACE)) {
        return;
    }

    if (evt.type !== "keydown") {
        uncapture();
        return;
    }

    // form fields handle their own arrows/typing; curgrade is the exception
    // (we focused it ourselves as the nav cursor)
    const ae = document.activeElement;
    if (is_form_field(ae) && ae !== curgrade) {
        return;
    }

    const key = event_key(evt), modkey = event_key.modcode(evt);
    if ((key === "ArrowUp" || key === "ArrowDown") && !modkey) {
        arrowcapture_arrow(evt, key);
    } else if ((key === "PageUp" || key === "PageDown") && !modkey) {
        arrowcapture_page(evt, key);
    } else if (key === "ArrowRight" && !modkey) {
        const liveel = curline && curline.element;
        if (liveel && enter_sidebar_gradebox_from(liveel)) {
            evt.preventDefault();
        }
    } else if (key === "Enter" && (!modkey || modkey === event_key.META)) {
        // let Enter activate a focused button/link/<summary>
        const liveel = curline && curline.element;
        if (ae && ae !== liveel
            && ae !== document.body
            && ae !== document.documentElement) {
            return;
        }
        arrowcapture_enter(evt);
    }
}

function arrowcapture_setfocus(what) {
    if (what.nodeName === "DIV"
        && hasClass(what, "pa-dl")) {
        what = Linediff.closest(what);
    }
    if (what.nodeName === "PA-LINEDIFF"
        && curline
        && curline.element === what.element) {
        return;
    }
    const ln = curline && curline.visible_source();
    if (ln) {
        removeClass(ln.element, "pa-dllive");
        ln.element.removeAttribute("tabindex");
    }
    if (what.nodeName === "PA-LINEDIFF") {
        curline = what;
        curgrade = null;
        what = what.element;
        addClass(what, "pa-dllive");
        what.tabIndex = 0;
    } else {
        curline = null;
        curgrade = what;
        requestAnimationFrame(() => what.select());
    }
    what.focus({preventScroll: true});
    return what;
}

function arrowcapture_focusat(what, evt) {
    what = arrowcapture_setfocus(what);
    const wf = what.closest(".pa-filediff-ctr"),
        marginTop = wf && wf.firstChild.nodeName === "H3" ? wf.firstChild.offsetHeight : 0;
    capture_scroll_at = performance.now();
    $(what).scrollIntoView({marginTop: marginTop, atCenter: true});
    evt.preventDefault();
}

function arrowcapture_arrow(evt, key) {
    const ln = curline && curline.visible_source();
    if (!ln && !curgrade) {
        uncapture();
        return;
    }
    const start = curgrade || ln.element,
        flags = Linediff.ANYFILE + (key === "ArrowDown" ? 0 : Linediff.BACKWARD) + Linediff.GRADES;
    let target = null;
    for (let lnx of Linediff.all(start, flags)) {
        if (lnx.nodeName !== "PA-LINEDIFF") {
            target = lnx;
            break;
        } else if (lnx.element !== start && lnx.is_visible() && lnx.is_source()) {
            target = lnx;
            break;
        }
    }
    if (target) {
        arrowcapture_focusat(target, evt);
    } else {
        // at boundary: bounce focus back so the next arrow isn't stolen
        const liveel = curline && curline.element;
        if (liveel) {
            liveel.focus({preventScroll: true});
        }
        evt.preventDefault();
    }
}

function scroll_parent(e) {
    let x = e.parentNode;
    while (x && x.tagName && $(x).css("overflow-y") === "visible") {
        x = x.parentNode;
    }
    return x && x.tagName ? x : window;
}

function arrowcapture_page(evt, key) {
    const ln = curline && curline.visible_source();
    if (!ln && !curgrade) {
        uncapture();
        return;
    }
    evt.preventDefault();
    const start = curgrade || ln.element,
        down = key === "PageDown",
        x = scroll_parent(start),
        win = x === window;
    // record the cursor's current screen position so it stays put as text scrolls
    const sr = start.getBoundingClientRect(),
        anchor = sr.top + sr.height / 2;
    // viewport geometry and current/max scroll
    let vheight, curscroll, maxscroll;
    if (win) {
        vheight = window.innerHeight;
        curscroll = window.scrollY;
        maxscroll = document.documentElement.scrollHeight - vheight;
    } else {
        vheight = x.clientHeight;
        curscroll = x.scrollTop;
        maxscroll = x.scrollHeight - x.clientHeight;
    }
    const wf = start.closest(".pa-filediff-ctr"),
        marginTop = wf && wf.firstChild.nodeName === "H3" ? wf.firstChild.offsetHeight : 0,
        goal = (vheight - marginTop) * (down ? 1 : -1); // screen distance to advance
    // Find the navigable line about one screenful away, measuring each line's
    // screen offset from the cursor. Picking the line first (rather than
    // scrolling a fixed amount and snapping) keeps the highlight from drifting:
    // we scroll by exactly the offset to that line, so it lands back on `anchor`.
    const flags = Linediff.ANYFILE + Linediff.GRADES + (down ? 0 : Linediff.BACKWARD);
    let target = null, targetdelta = 0, bestdist = Infinity;
    for (let lnx of Linediff.all(start, flags)) {
        let el;
        if (lnx.nodeName !== "PA-LINEDIFF") {
            el = lnx;
        } else if (lnx.element !== start && lnx.is_visible() && lnx.is_source()) {
            el = lnx.element;
        } else {
            continue;
        }
        const r = el.getBoundingClientRect(),
            delta = r.top + r.height / 2 - anchor,
            dist = Math.abs(delta - goal);
        if (dist <= bestdist) {
            bestdist = dist;
            target = lnx;
            targetdelta = delta;
        } else {
            break; // |delta - goal| only grows from here
        }
    }
    if (!target) {
        // at boundary: bounce focus back so the next arrow isn't stolen
        const liveel = curline && curline.element;
        if (liveel) {
            liveel.focus({preventScroll: true});
        }
        return;
    }
    const pos = Math.max(0, Math.min(curscroll + targetdelta, maxscroll));
    if (win) {
        window.scrollTo(window.scrollX, pos);
    } else {
        x.scrollTop = pos;
    }
    capture_scroll_at = performance.now();
    arrowcapture_setfocus(target);
}

function arrowcapture_enter(evt) {
    let ln = curline && curline.visible_source();
    if (!ln) {
        uncapture();
        return;
    }
    curline = ln;
    curgrade = null;
    evt.preventDefault();
    capture_scroll_at = performance.now();
    make_linenote();
}

function arm_arrowcapture() {
    $(".pa-filediff").removeClass("live");
    capture_scroll_at = performance.now();
    $(document).off(".pa-linenote");
    $(window).off(".pa-linenote");
    $(document).on("keydown.pa-linenote", arrowcapture);
    $(window).on("scroll.pa-linenote", arrowcapture);
}

function capture(tr) {
    for (const el of document.querySelectorAll(".pa-dllive")) {
        if (el !== tr) {
            removeClass(el, "pa-dllive");
            el.removeAttribute("tabindex");
        }
    }
    if (!hasClass(tr, "pa-gw")) {
        addClass(tr, "pa-dllive");
        tr.tabIndex = 0;
        tr.focus();
    }
    arm_arrowcapture();
}

// Focusing an inline grade input makes it the nav cursor (curgrade).
// evt.target === this filters ancestor matches: markdown/text gc types
// put .pa-gradevalue on the container, which can hold inner .pa-dl lines.
function inline_grade_focusin(evt) {
    if (evt.target !== this || this.closest(".pa-sidebar")) {
        return;
    }
    for (const el of document.querySelectorAll(".pa-dllive")) {
        removeClass(el, "pa-dllive");
        el.removeAttribute("tabindex");
    }
    curline = null;
    curgrade = this;
    arm_arrowcapture();
}

// Drop curgrade when focus walks away to something that isn't another
// grade input. Less sticky than curline.
function inline_grade_focusout(evt) {
    if (evt.target !== this || curgrade !== this) {
        return;
    }
    const rt = evt.relatedTarget;
    if (rt && rt.closest && rt.closest(".pa-gradevalue")) {
        return;
    }
    curgrade = null;
}

$(document).on("focusin", ".pa-gradevalue", inline_grade_focusin);
$(document).on("focusout", ".pa-gradevalue", inline_grade_focusout);

function uncapture() {
    for (const tr of document.querySelectorAll(".pa-dllive")) {
        removeClass(tr, "pa-dllive");
        tr.removeAttribute("tabindex");
    }
    curline = null;
    curgrade = null;
    $(".pa-filediff").addClass("live");
    $(document).off(".pa-linenote");
    $(window).off(".pa-linenote");
}

function line_at_viewport_fraction(frac, vh) {
    const vw = window.innerWidth || document.documentElement.clientWidth,
        y = Math.round(vh * frac);
    for (const fx of [0.5, 0.35, 0.65, 0.2, 0.8]) {
        const el = document.elementFromPoint(Math.round(vw * fx), y);
        if (el && el.closest(".pa-filediff-ctr")) {
            return el.closest(".pa-dl") || el;
        }
    }
    return null;
}

function adjacent_visible_source(ln, backward) {
    for (const lnx of Linediff.all(ln, backward ? Linediff.BACKWARD : 0)) {
        if (lnx.element !== ln && lnx.is_visible() && lnx.is_source()) {
            return lnx.element;
        }
    }
    return null;
}

// Capture the current logical scroll position and return a closure that
// restores it. Use around diff display toggles (markdown, hide-left, comments,
// collapsing files), which show or hide lines and change line heights, making
// the viewport jump when content above the fold changes size: call
// `active_scroll_anchor()` before the toggle and invoke the returned closure
// afterward. The primary anchor is the live line if one is on screen, otherwise
// the line about 15% down the viewport; while visible, it is held at its
// original screen position. If the toggle hides it (e.g. its file was
// collapsed), liveness moves to the nearest following visible line and the
// topmost still-visible reference line is held fixed instead — keeping visible
// content put rather than pulling distant content up to the anchor. If no
// source line in the anchor's file remains visible (e.g. SVG source replaced by
// its image), the file's header is held fixed, with content starting below it.
export function active_scroll_anchor() {
    const vh = window.innerHeight || document.documentElement.clientHeight;

    let anchorel = null, anchor_is_live = false;
    const liveel = document.querySelector(".pa-dllive");
    if (liveel) {
        const r = liveel.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) {
            anchorel = liveel;
            anchor_is_live = true;
        }
    }
    anchorel = anchorel || line_at_viewport_fraction(0.15, vh);
    if (!anchorel) {
        return function () {};
    }

    let anchorel2 = null;
    if (anchorel && hasClass(anchorel, "pa-dlr")) {
        anchorel2 = anchorel.nextSibling;
    }

    const anchor_top = anchorel.getBoundingClientRect().top;

    // file content top, clamped below its (possibly sticky) header
    const fdel = anchorel.closest(".pa-filediff");
    let fd_top = 0;
    if (fdel) {
        const h3 = fdel.previousSibling;
        fd_top = fdel.getBoundingClientRect().top;
        if (h3 && hasClass(h3, "pa-fileref")) {
            fd_top = Math.max(fd_top, h3.getBoundingClientRect().bottom);
        }
    }

    return function () {
        if (!anchorel.parentElement) {
            anchorel = anchorel2;
        }
        let target_top = anchor_top;
        if (!anchorel.offsetParent) {
            if (hasClass(anchorel, "pa-dl")
                && anchorel.parentElement.offsetParent) {
                anchorel = adjacent_visible_source(anchorel, true)
                    || adjacent_visible_source(anchorel, false);
            } else {
                while (!anchorel.offsetParent) {
                    anchorel = anchorel.parentElement;
                }
            }
            if ((!anchorel || anchorel === fdel)
                && fdel && fdel.offsetParent) {
                anchorel = fdel;
                target_top = fd_top;
            }
        }
        if (anchorel) {
            const delta = Math.round(anchorel.getBoundingClientRect().top - target_top);
            if (delta) {
                capture_scroll_at = performance.now();
                window.scrollBy(0, delta);
                global_tooltip && global_tooltip.reposition();
            }
        }
        if (anchor_is_live) {
            if (anchorel && hasClass(anchorel, "pa-dl")) {
                arrowcapture_setfocus(anchorel);
            } else {
                const live = document.querySelector(".pa-dllive");
                live && removeClass(live, "pa-dllive");
                curline = null;
            }
        }
    }
}

function unedit(note) {
    const done = note.render(true),
        ctr = curline && curline.visible_source();
    ctr && capture(ctr.element);
    return done;
}

function pa_save_my_note(elt) {
    if (!hasClass(elt, "pa-gw")) {
        throw new Error("bad `elt` in pa_save_my_note");
    } else if (!hasClass(elt, "pa-outstanding")) {
        const f = $(elt).find("form")[0],
            text = f.elements.note.value,
            iscomment = f.elements.iscomment && f.elements.iscomment.checked;
        $(f).find(".pa-save-message").remove();
        $(f).find(".aab").append('<div class="aabut pa-save-message">Saving…</div>');
        Note.at(elt).save_text(text, iscomment).then(() => {
            const ctr = curline ? curline.visible_source() : null;
            ctr && capture(ctr.element);
        });
    }
}

function cancel() {
    unedit(Note.closest(this).cancel_edit());
    return true;
}

function textarea_keydown(evt) {
    if (event_key(evt) === "Escape" && !event_key.modcode(evt) && unedit(Note.closest(this))) {
        return false;
    } else if (event_key(evt) === "Enter" && event_key.modcode(evt) === event_key.META) {
        $(this).closest("form").submit();
        return false;
    }
    return true;
}

function nearby(dx, dy) {
    return (dx * dx) + (dy * dy) < 144;
}

function pa_linenote(event) {
    const dl = event.target.closest(".pa-dl");
    if (event.button !== 0
        || !dl
        || hasClass(dl, "pa-gx")
        || event.target.matches("button, a, textarea, input, label")
        || event.target.closest(".pa-note-suggestions")) {
        return;
    }
    const line = locate(event.target),
        t = new Date().getTime();
    if (event.type === "mousedown" && line) {
        if (curline
            && curline.element === line.element
            && down_event
            && nearby(down_event[0] - event.clientX, down_event[1] - event.clientY)
            && t - down_event[2] <= 500) {
            // skip
        } else {
            curline = line;
            curgrade = null;
            down_event = [event.clientX, event.clientY, t, false];
        }
    } else if (event.type === "mouseup" && line) {
        if (curline
            && curline.element === line.element
            && down_event
            && nearby(down_event[0] - event.clientX, down_event[1] - event.clientY)
            && !down_event[3]) {
            curline = line;
            curgrade = null;
            down_event[3] = true;
            make_linenote(event);
        }
    } else if (event.type === "click" && line) {
        curline = line;
        curgrade = null;
        make_linenote(event);
    } else {
        curline = null;
        curgrade = null;
        down_event = null;
    }
}

function make_linenote(event) {
    const note = Note.near(curline), tr = note.force_element();
    capture_scroll_at = performance.now();
    if (hasClass(tr, "editing")) {
        if (unedit(note)) {
            event && event.stopPropagation();
            return true;
        }
        const $ta = $(tr).find("textarea").focus();
        $ta[0].setSelectionRange && $ta[0].setSelectionRange(0, $ta.val().length);
        return false;
    }
    capture(curline.element);
    render_form($(tr), note, true);
    return false;
}

handle_ui.on("pa-editablenotes", pa_linenote);


function my_note_feedback(ln) {
    const uid = siteinfo.user.cid;
    if (ln.like && ln.like.indexOf(uid) >= 0) {
        return 1;
    } else if (ln.dislike && ln.dislike.indexOf(uid) >= 0) {
        return -1;
    }
    return 0;
}

function display_note_suggestions(form, ns) {
    let suggctr = form.querySelector(".pa-note-suggestions"),
        searchre;
    if (!suggctr) {
        const aa = form.querySelector(".pa-note-aa"),
            search = document.createElement("input");
        search.type = "search";
        search.className = "uikd uii pa-search-suggestions mt-4 mb-3";
        search.placeholder = "Search…";
        suggctr = document.createElement("div");
        suggctr.className = "pa-note-suggestions";
        aa.before(search, suggctr);
    } else {
        const search = form.querySelector(".pa-search-suggestions");
        searchre = search_suggestions_re(search ? search.value : "");
    }
    const known = new Set;
    for (let ch = suggctr.firstChild; ch; ch = ch.nextSibling) {
        known.add(ch.getAttribute("data-content"));
    }
    for (const n of ns) {
        if (known.has(n.ftext)) {
            continue;
        }
        const mf = my_note_feedback(n),
            li = document.createElement("div"),
            gf = (n.like || []).length - (n.dislike || []).length;
        li.className = "pa-note-suggestion".concat(mf < 0 || (mf === 0 && gf < 0) ? " dim" : "");
        li.setAttribute("data-content", n.ftext);
        if (searchre && !searchre.test(n.ftext)) {
            li.hidden = true;
        }
        suggctr.appendChild(li);
        const bbox = document.createElement("div"),
            b1 = document.createElement("button"),
            b2 = document.createElement("button"),
            tx = document.createElement("div");
        bbox.className = "btnbox small";
        bbox.append(b1, b2);
        tx.className = "flex-grow-1";
        li.append(bbox, tx);
        b1.type = b2.type = "button";
        b1.className = "ui pa-use-suggestion like".concat(mf > 0 ? " taken" : "");
        b1.setAttribute("aria-label", "Use");
        b1.textContent = "✔️";
        b2.className = "ui pa-use-suggestion dislike".concat(mf < 0 ? " taken" : "");
        b2.setAttribute("aria-label", "Downrank");
        b2.textContent = "➖";
        render_onto(tx, "f", n.ftext);
        tooltip.call(b1);
        tooltip.call(b2);
    }
}

function my_note_compare(a, b) {
    if (a.status !== b.status) {
        return a.status > b.status ? -1 : 1;
    } else if (a.ftext.substring(0, 3) === b.ftext.substring(0, 3)) {
        return a.ftext.localeCompare(b.ftext);
    }
    const af = ftext.parse(a.ftext), bf = ftext.parse(b.ftext);
    return af.localeString(bf);
}

function note_suggestions(form, suggdata) {
    display_note_suggestions(form, suggdata.notelist);
    $(form).find(".pa-load-note-suggestions").prop("disabled", !suggdata.more);
}

handle_ui.on("pa-load-note-suggestions", function () {
    const form = this.closest("form");
    let suggdata = $(form).data("paNoteSuggestions");
    if (suggdata && !suggdata.more) {
        note_suggestions(form, suggdata);
        return;
    }
    const ld = Linediff.closest(this),
        args = {file: ld.file, pset: ld.pset},
        gi = GradeSheet.closest(this);
    if (!gi || !gi.base_commit || gi.base_handout) {
        args.linea = ld.linea;
    } else if (gi.user && gi.commit) {
        let x = ld.lineb;
        if (x !== null) {
            args.u = gi.user;
            args.line = "b" + x;
            args.commit = gi.commit;
        }
    }
    if (!suggdata) {
        args.neighborhood = 5;
        args.my_neighborhood = 20;
    } else if (suggdata.neighborhood >= 0 && suggdata.neighborhood <= 5) {
        args.neighborhood = 20;
        args.my_neighborhood = -1;
    } else {
        args.neighborhood = -1;
    }
    this.disabled = true;
    $.ajax(hoturl("api/linenotesuggest", args), {
        success: function (data) {
            if (!data.ok) {
                return;
            }
            data.notelist = data.notelist || [];
            for (const note of data.notelist) {
                const mf = my_note_feedback(note),
                    gf = (note.like || []).length - (note.dislike || []).length;
                if (mf > 0 && gf > 1) {
                    note.status = 3;
                } else if (mf > 0) {
                    note.status = 2;
                } else if (mf === 0 && gf > 0) {
                    note.status = 1;
                } else if (mf === 0 && gf === 0) {
                    note.status = 0;
                } else if (mf === -1 && gf >= 0) {
                    note.status = -1;
                } else {
                    note.status = -2;
                }
            }
            data.notelist.sort(my_note_compare);
            $(form).data("paNoteSuggestions", data);
            note_suggestions(form, data);
        }
    });
});

function linenotemark(context, mark) {
    const ld = Linediff.closest(context);
    $.ajax(hoturl("=api/linenotemark", {file: ld.file, linea: ld.linea, mark: mark, pset: ld.pset}),
        { data: { ftext: context.getAttribute("data-content") }, method: "POST" });
}

handle_ui.on("pa-use-suggestion", function () {
    const e = this.closest(".pa-note-suggestion"),
        f = e.closest("form");
    if (this.classList.contains("like")) {
        linenotemark(e, "like");
        f.elements.note.value = ftext.parse(e.getAttribute("data-content"))[1];
    } else {
        linenotemark(e, "dislike");
        e.remove();
    }
});

function search_suggestions_re(value) {
    let patterns = "";
    for (const str of value.split(/\s+/)) {
        str !== "" && (patterns = patterns.concat("(?=.*", regexp_quote(str), ")"));
    }
    return patterns ? new RegExp(patterns, "i") : null;
}

handle_ui.on("pa-search-suggestions", function (event) {
    if (event.type === "input") {
        const el = this.nextSibling, regex = search_suggestions_re(this.value);
        for (let ne = el.firstChild; ne; ne = ne.nextSibling) {
            const hidden = regex && !regex.test(ne.getAttribute("data-content"));
            ne.hidden = !!hidden;
        }
    } else if (event.type === "keydown"
               && !(event_key.modcode(event) & (event_key.SHIFT | event_key.ALT))
               && event_key(event) === "Enter") {
        event.preventDefault();
    }
});
